export interface PriorAuthCptLine {
  cptCode: string;
  authorizedUnits: number;
  cptCodeId?: string;
  priorAuthCptId?: string;
  /** UI-only; not sent to the prior-auth API. */
  description?: string;
}

export interface AvailablePriorAuthCptLine {
  priorAuthCptId?: string;
  cptCode: string;
  cptCodeId?: string;
  authorizedUnits: number;
  usedUnits: number;
  remainingUnits: number;
}

export interface AvailablePriorAuth {
  priorAuthId: string;
  authorizationNumber: string;
  authorizationDate?: string;
  authorizationExpiry?: string;
  coverageId?: string;
  patientId?: string;
  payerName: string;
  cptLines: AvailablePriorAuthCptLine[];
}

export interface PriorAuthSaveRequest {
  /** Required when updating an existing prior auth. */
  priorAuthId?: string;
  patientId: string;
  coverageId: string;
  cptLines: PriorAuthCptLine[];
  procedureId: string;
  encounterId: string;
  authorizationSource: string;
  authorizationDate: string;
  authorizationNumber: string;
  referenceName?: string;
  documentId?: string;
  referenceNumber?: string;
  notes?: string;
  authorizationForDiagnostic?: boolean;
  authorizationExpiry: string;
  /** Source prior auth when this record was applied from available authorizations. */
  parentPriorAuthId?: string;
  authType: "auth" | "denial";
  denialStatus?: "final" | "appeal";
}

export interface PriorAuthConsumeRequest {
  priorAuthCptId: string;
  procedureId: string;
  encounterId: string;
  coverageId: string;
  unitsUsed: number;
  dos: string;
}

export type PriorAuthApiRecord = PriorAuth & {
  priorAuthId?: string;
};

/** Item from POST `/api/v1/prior-auth/procedure/batch`. */
export interface ProcedurePriorAuthsBatchItem {
  procedureId: string;
  priorAuths: PriorAuthApiRecord[];
}

export class PriorAuth {
  id: string;
  /** API list responses may use priorAuthId instead of id. */
  priorAuthId?: string;
  patientId?: string;
  coverageId?: string;
  procedureId: string;
  encounterId: string;
  /** @deprecated Use cptLines[].authorizedUnits instead. */
  units?: number;
  authorizationSource: string;
  authorizationDate: string;
  authorizationNumber: string;
  authorizationExpiry: string;
  authorizationForDiagnostic: boolean;
  referenceName: string;
  referenceNumber: string;
  /** Derived from cptLines for display and legacy callers. */
  primaryCpt?: string[];
  cptLines?: PriorAuthCptLine[];
  notes: string;
  documentId: string;
  /** Source prior auth when applied from available authorizations. */
  parentPriorAuthId?: string;
  authType?: "auth" | "denial";
  denialStatus?: "final" | "appeal" | "";
  /** Biller API uses `createAt`; some responses may expose `createdAt`. */
  createAt?: string;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  /** @deprecated Legacy API shape. */
  priorAuthUnits?: priorAuthUnits[];
  constructor(appointmentId: string, procedureId: string) {
    this.procedureId = procedureId;
    this.encounterId = appointmentId;
    this.authorizationSource = "";
  }
}

export class priorAuthUnits {
  dos: string;
  units: 0;
  memberNumber: string;
  groupNumber: string;
  coverageId: string;
}

export function getPriorAuthCptCodes(
  priorAuth: PriorAuth | null | undefined
): string[] {
  if (!priorAuth) {
    return [];
  }
  if (priorAuth.cptLines?.length) {
    return priorAuth.cptLines
      .map((line) => line.cptCode)
      .filter((code) => !!code?.trim());
  }
  return priorAuth.primaryCpt ?? [];
}

export function normalizePriorAuth(
  raw: PriorAuthApiRecord | null | undefined
): PriorAuth | null {
  if (!raw) {
    return null;
  }
  const normalized = { ...raw } as PriorAuth;
  const ext = raw as PriorAuthApiRecord & Record<string, unknown>;
  if (!normalized.id && normalized.priorAuthId) {
    normalized.id = normalized.priorAuthId;
  }
  if (!normalized.createdAt && ext["createAt"]) {
    normalized.createdAt = String(ext["createAt"]);
  }
  if (!normalized.createAt && normalized.createdAt) {
    normalized.createAt = normalized.createdAt;
  }
  if (Array.isArray(normalized.cptLines)) {
    normalized.primaryCpt = normalized.cptLines.map((line) => line.cptCode);
    const totalUnits = normalized.cptLines.reduce(
      (sum, line) => sum + (Number(line.authorizedUnits) || 0),
      0
    );
    if (totalUnits > 0) {
      normalized.units = totalUnits;
    }
  } else if (normalized.primaryCpt?.length) {
    normalized.cptLines = normalized.primaryCpt.map((code) => ({
      cptCode: code,
      authorizedUnits: normalized.units ?? 1,
    }));
  }
  if (normalized.authorizationForDiagnostic == null) {
    normalized.authorizationForDiagnostic = false;
  }
  return normalized;
}

export function mergePriorAuthCptLines(
  priorAuth: PriorAuth,
  cptLines: PriorAuthCptLine[]
): PriorAuth {
  const normalized = normalizePriorAuth(priorAuth) ?? priorAuth;
  normalized.cptLines = cptLines.map((line) => ({ ...line }));
  normalized.primaryCpt = cptLines
    .map((line) => line.cptCode)
    .filter((code) => !!code?.trim());
  const totalUnits = cptLines.reduce(
    (sum, line) => sum + (Number(line.authorizedUnits) || 0),
    0
  );
  normalized.units = totalUnits > 0 ? totalUnits : undefined;
  return normalized;
}

export function normalizePriorAuthList(
  raw: PriorAuthApiRecord[] | PriorAuthApiRecord | null | undefined
): PriorAuth[] {
  if (!raw) {
    return [];
  }
  const list = Array.isArray(raw) ? raw : [raw];
  return list
    .map((item) => normalizePriorAuth(item))
    .filter((item): item is PriorAuth => !!item);
}

export function getPriorAuthCptCodesFromList(
  priorAuths: PriorAuth[] | null | undefined
): string[] {
  if (!priorAuths?.length) {
    return [];
  }
  const codes = new Set<string>();
  priorAuths.forEach((auth) => {
    getPriorAuthCptCodes(auth).forEach((code) => {
      if (code?.trim()) {
        codes.add(code);
      }
    });
  });
  return Array.from(codes);
}

export function hasSavedPriorAuths(
  priorAuths: PriorAuth[] | null | undefined,
  fallback?: PriorAuth | null
): boolean {
  if (priorAuths?.some((auth) => !!auth?.id)) {
    return true;
  }
  return !!fallback?.id;
}

export function getSavedPriorAuths(
  priorAuths: PriorAuth[] | null | undefined,
  fallback?: PriorAuth | null
): PriorAuth[] {
  const saved = (priorAuths ?? []).filter((auth) => !!auth?.id);
  if (saved.length) {
    return saved;
  }
  return fallback?.id ? [fallback] : [];
}

/** True when at least one saved prior auth exists for the encounter (any encounter if id omitted). */
export function hasSavedPriorAuthForEncounter(
  priorAuths: PriorAuth[] | null | undefined,
  fallback: PriorAuth | null | undefined,
  encounterId: string | null | undefined
): boolean {
  const saved = getSavedPriorAuths(priorAuths, fallback);
  if (!saved.length) {
    return false;
  }
  const eid = String(encounterId ?? "").trim();
  if (!eid) {
    return true;
  }
  return saved.some(
    (auth) => String(auth.encounterId ?? "").trim() === eid
  );
}

export type OverallPriorAuthStatus = "Authorized" | "Denied";

export function isPriorAuthDenial(
  auth: PriorAuth | null | undefined
): boolean {
  return auth?.authType === "denial";
}

/**
 * Overall auth status from saved records:
 * - At least one auth (non-denial) → Authorized
 * - All denials → Denied
 * - No saved records → null
 */
export function getOverallPriorAuthStatus(
  priorAuths: PriorAuth[] | null | undefined,
  fallback?: PriorAuth | null
): OverallPriorAuthStatus | null {
  const saved = getSavedPriorAuths(priorAuths, fallback);
  if (!saved.length) {
    return null;
  }
  if (saved.some((auth) => !isPriorAuthDenial(auth))) {
    return "Authorized";
  }
  return "Denied";
}

export function isPriorAuthDiagnosticOnly(
  auth: PriorAuth | null | undefined
): boolean {
  return auth?.authorizationForDiagnostic === true;
}

/** Human-readable auth type for non-denial records. */
export function getPriorAuthTypeDisplayLabel(
  auth: PriorAuth | null | undefined
): string | null {
  if (!auth || isPriorAuthDenial(auth)) {
    return null;
  }
  return isPriorAuthDiagnosticOnly(auth) ? "Diagnostic" : "Intervention";
}

/** Summary card / header: prefer the first saved authorization (not a denial). */
export function getPrimaryDisplayPriorAuth(
  priorAuths: PriorAuth[] | null | undefined,
  fallback?: PriorAuth | null
): PriorAuth | null {
  const saved = getSavedPriorAuths(priorAuths, fallback);
  if (!saved.length) {
    return null;
  }
  return saved.find((auth) => !isPriorAuthDenial(auth)) ?? saved[0];
}

/**
 * Procedure room badge: show only when every saved authorization (non-denial) is diagnostic.
 * Denials are ignored — they do not carry authorizationForDiagnostic.
 * If any auth is procedure (or unset/legacy), treat the procedure as not diagnostic only.
 */
export function areAllPriorAuthsDiagnosticOnly(
  priorAuths: PriorAuth[] | null | undefined,
  fallback?: PriorAuth | null
): boolean {
  const saved = getSavedPriorAuths(priorAuths, fallback);
  const authorizations = saved.filter((auth) => !isPriorAuthDenial(auth));
  if (!authorizations.length) {
    return false;
  }
  return authorizations.every((auth) => isPriorAuthDiagnosticOnly(auth));
}

/**
 * Build POST `/api/v1/prior-auth` body for create or update (auth or denial).
 * Prefer `options.procedureId` / `options.encounterId` when the draft auth is missing them.
 */
export function buildPriorAuthSaveRequest(
  priorAuth: PriorAuth,
  cptLines: PriorAuthCptLine[],
  patientId: string,
  coverageId: string,
  authType: "auth" | "denial" = "auth",
  options?: { procedureId?: string; encounterId?: string }
): PriorAuthSaveRequest {
  const priorAuthId = (priorAuth.id || priorAuth.priorAuthId || "").trim();
  const procedureId = (
    options?.procedureId ??
    priorAuth.procedureId ??
    ""
  ).trim();
  const encounterId = (
    options?.encounterId ??
    priorAuth.encounterId ??
    ""
  ).trim();
  return {
    ...(priorAuthId ? { priorAuthId } : {}),
    patientId,
    coverageId,
    cptLines: cptLines.map((line) => ({
      cptCode: line.cptCode,
      authorizedUnits: Number(line.authorizedUnits) || 1,
      ...(line.cptCodeId ? { cptCodeId: line.cptCodeId } : {}),
      ...(line.priorAuthCptId ? { priorAuthCptId: line.priorAuthCptId } : {}),
    })),
    procedureId,
    encounterId,
    authorizationSource:
      authType === "denial" ? "" : (priorAuth.authorizationSource ?? ""),
    authorizationDate: priorAuth.authorizationDate ?? "",
    authorizationNumber: priorAuth.authorizationNumber ?? "",
    referenceName: priorAuth.referenceName,
    documentId: priorAuth.documentId,
    referenceNumber: priorAuth.referenceNumber,
    notes: priorAuth.notes,
    authorizationForDiagnostic:
      authType === "denial" ? false : priorAuth.authorizationForDiagnostic,
    authorizationExpiry:
      authType === "denial" ? "" : (priorAuth.authorizationExpiry ?? ""),
    ...(priorAuth.parentPriorAuthId?.trim()
      ? { parentPriorAuthId: priorAuth.parentPriorAuthId.trim() }
      : {}),
    authType,
    ...(authType === "denial" && priorAuth.denialStatus
      ? { denialStatus: priorAuth.denialStatus }
      : {}),
  };
}
